// Content for the service and city landing pages.
// Each entry renders through ServicePage.jsx. City content lives in src/content/cities/.

import ALL_CITIES from "../../../content/cities";

export const SITE_URL = "https://www.quickvents.com";
export const BUSINESS_NAME = "Quick Vents and Appliance Repair";
export const BOOKING_URL =
    "https://book.housecallpro.com/book/Quick-Appliance-Services-and-Dryer-Vent-Cleaning/1d772c9908b14627bf68d004ecf2882d";
export const PHONE_DISPLAY = "(561) 929-5476";
export const PHONE_TEL = "tel:+15619295476";

export const SERVICE_LINKS = [
    { label: "Dryer Vent Cleaning", path: "/dryer-vent-cleaning" },
    { label: "Appliance Repair", path: "/appliance-repair" },
];

const VENT_SIGNS = [
    "Clothes need more than one cycle to dry",
    "The dryer or laundry room feels unusually hot",
    "A burning or musty smell when the dryer runs",
    "Lint collecting around the lint trap, door, or behind the dryer",
    "The outside vent flap barely opens while the dryer is running",
    "It has been more than a year since the vent was last cleaned",
];

const VENT_INCLUDED = [
    "Full inspection from the dryer to the outside vent",
    "Rotary brush and high-powered vacuum cleaning of the entire duct",
    "Exterior vent hood cleared of lint, debris, and nests",
    "Lint trap housing vacuumed out",
    "Transition hose behind the dryer cleared",
    "Airflow tested after cleaning",
];

const VENT_HIGHLIGHTS = [
    { icon: "/assets/icons/flame.png", title: "Lower fire risk", desc: "Removes the flammable lint that builds up inside the duct." },
    { icon: "/assets/icons/time.png", title: "Faster drying", desc: "Restored airflow means most loads dry in a single cycle." },
    { icon: "/assets/icons/energy.png", title: "Lower energy use", desc: "A dryer that breathes runs shorter and uses less power." },
    { icon: "/assets/icons/dust.png", title: "Longer dryer life", desc: "Less strain on the motor, heating element, and belts." },
];

const VENT_STEPS = [
    { title: "Book online or call", desc: "Pick a time that works for you, or call (561) 929-5476." },
    { title: "Full vent inspection", desc: "We trace the run from the dryer to the outside hood or roof cap." },
    { title: "Brush and vacuum", desc: "Rotary brushes and a vacuum clear the duct." },
    { title: "Airflow check", desc: "We confirm the vent is moving air and share what we found." },
];

const VENT_CHECKLIST_HEAD = {
    kicker: "What to Expect",
    heading: "Know the signs, and what we do about them",
    lead: "Spot a clogged vent early, and see exactly what's included when we clean it.",
    cta: "Schedule a dryer vent cleaning",
};

const VENT_FAQS = [
    {
        question: "How often should I have my dryer vent cleaned?",
        answer: "Most homes should have the dryer vent cleaned at least once a year. Large households, homes with pets, and long or roof-mounted vent runs often need cleaning every six months.",
    },
    {
        question: "How long does a dryer vent cleaning take?",
        answer: "Most dryer vent cleanings take about an hour. Longer vent runs, roof terminations, or heavily clogged ducts can take a little more time.",
    },
    {
        question: "Can a clogged dryer vent cause a fire?",
        answer: "Yes. Lint is highly flammable, and failure to clean is the leading cause of home clothes dryer fires according to the U.S. Fire Administration. Regular cleaning removes that built-up lint and lowers the risk.",
    },
    {
        question: "Will cleaning my dryer vent lower my energy bill?",
        answer: "A clogged vent forces your dryer to run longer and hotter to dry each load. Restoring airflow shortens drying times, which uses less energy and puts less wear on the dryer.",
    },
];

// Live cities only; a city file with draft: true is left out everywhere.
const CITIES = ALL_CITIES.filter((c) => !c.draft && c.slug && c.name);

const cityPath = (slug) => `/dryer-vent-cleaning/${slug}`;

export const CITY_LINKS = CITIES.map((c) => ({
    label: c.name,
    path: cityPath(c.slug),
    county: c.county,
}));

// City links grouped by county, in the order counties should appear.
export const CITY_GROUPS = ["Palm Beach", "Broward"]
    .map((county) => ({ county: `${county} County`, links: CITY_LINKS.filter((c) => c.county === county) }))
    .filter((g) => g.links.length > 0);

export const SERVICE_AREA_NAMES = CITIES.map((c) => c.name);

// "A, B, and C"
const listNames = (names) => (names.length < 3 ? names.join(" and ") : `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`);

const dryerVentCleaning = {
    path: "/dryer-vent-cleaning",
    serviceName: "Dryer Vent Cleaning",
    title: `Dryer Vent Cleaning in Boca Raton & South Florida | ${BUSINESS_NAME}`,
    description:
        "Professional dryer vent cleaning in Boca Raton and across Palm Beach and Broward Counties. Reduce fire risk, dry clothes faster, and lower energy use. Call (561) 929-5476.",
    eyebrow: "Dryer Vent Cleaning",
    h1: "Dryer Vent Cleaning in South Florida",
    intro: "A clogged dryer vent makes your dryer work harder, run hotter, and take longer to dry every load, and it is a real fire risk. Quick Vents and Appliance Repair cleans the full vent run so your dryer can breathe again.",
    bookingCard: {
        heading: "Get your dryer vent cleaned",
        points: ["Full vent run cleaned, roof vents included", "Airflow tested before we leave", "Clear quote before we start"],
    },
    highlights: {
        heading: "Why dryer vent cleaning matters",
        lead: [
            "Lint your trap misses builds up in the vent until hot air can't escape. It's also the leading cause of home dryer fires, per the U.S. Fire Administration.",
        ],
        items: VENT_HIGHLIGHTS,
        cta: "Protect your home today",
    },
    sections: [],
    steps: VENT_STEPS,
    stepsLead: "From booking to the final airflow check, here's exactly what happens.",
    listBlocks: [
        { heading: "Signs your dryer vent needs cleaning", items: VENT_SIGNS },
        { heading: "What's included in every cleaning", items: VENT_INCLUDED },
    ],
    checklistHead: VENT_CHECKLIST_HEAD,
    showCities: true,
    faqs: VENT_FAQS,
};

const applianceRepair = {
    path: "/appliance-repair",
    serviceName: "Appliance Repair",
    title: `Appliance Repair in Boca Raton & South Florida | ${BUSINESS_NAME}`,
    description:
        "Fast, reliable appliance repair in Boca Raton and across Palm Beach and Broward Counties. Dryers, washers, refrigerators, ovens, and dishwashers. Call (561) 929-5476.",
    eyebrow: "Appliance Repair",
    h1: "Appliance Repair in South Florida",
    intro: "When a major appliance breaks down, your whole routine stops. Quick Vents and Appliance Repair diagnoses the problem, explains your options, and gets your appliance working again with as little disruption as possible.",
    bookingCard: {
        heading: "Get your appliance fixed",
        points: ["Dryers, washers, fridges, ovens, dishwashers", "Diagnosis and a clear quote first", "Tested before we leave"],
    },
    highlights: {
        heading: "Appliances we repair",
        cta: "Fix my appliance",
        lead: [
            "We repair the household appliances you rely on every day. Every repair starts with a diagnostic service call, and you get a clear quote before any repair work begins.",
        ],
        items: [
            { icon: "/assets/icons/dryer.png", title: "Dryers", desc: "No heat, long dry times, shutting off early, or loud noises." },
            { icon: "/assets/icons/tshirt.png", title: "Washers", desc: "Won't drain, spin, or fill, leaks, and error codes." },
            { icon: "/assets/icons/fridge.png", title: "Refrigerators", desc: "Not cooling, leaking, ice maker problems, and noisy fans." },
            { icon: "/assets/icons/flame.png", title: "Ovens and ranges", desc: "Burners, bake elements, and igniters that won't heat." },
        ],
    },
    sections: [],
    listBlocks: [
        {
            heading: "Common issues",
            items: [
                "Dryers that won't heat, take too long, or shut off early",
                "Washers that won't drain, spin, or fill",
                "Refrigerators that aren't cooling or are leaking",
                "Ovens and ranges with burners or elements that won't heat",
                "Dishwashers that won't drain or clean",
            ],
        },
        {
            heading: "What's included in every repair",
            items: [
                "Diagnosis by a trained technician",
                "Clear quote before any work begins",
                "Repair completed and appliance tested",
                "Vent check with every dryer repair",
                "Work area left clean",
            ],
        },
    ],
    checklistHead: {
        kicker: "What to Expect",
        heading: "Common problems, and what every repair includes",
        lead: "From dryers to dishwashers, every repair starts with a diagnosis and a clear quote.",
        cta: "Schedule an appliance repair",
    },
    stepsLead: "From your first call to the final test, here's exactly what happens.",
    steps: [
        { title: "Book online or call", desc: "Tell us which appliance and what it's doing." },
        { title: "Diagnosis", desc: "A technician finds the cause of the problem." },
        { title: "Clear quote", desc: "You approve the price before any repair work begins." },
        { title: "Repair and test", desc: "We fix it and test the appliance before we leave." },
    ],
    showCities: true,
    faqs: [
        {
            question: "What appliances do you repair?",
            answer: "We repair dryers, washers, refrigerators, ovens and ranges, dishwashers, and other common household appliances.",
        },
        {
            question: "How much does appliance repair cost?",
            answer: "Every repair starts with a diagnostic service call. Our technician finds the problem and gives you a clear quote before any repair work is done, so there are no surprises.",
        },
        {
            question: "Should I repair or replace my appliance?",
            answer: "It depends on the age of the appliance and the cost of the repair. After diagnosing the problem, our technician will give you an honest recommendation so you can decide what makes sense.",
        },
        {
            question: "Can you repair my dryer and clean the vent in one visit?",
            answer: "Yes. Many dryer problems, like long drying times, overheating, or shutting off mid-cycle, are caused by a clogged vent. We can fix the dryer and clear the vent in the same visit so the problem doesn't come back.",
        },
        {
            question: "What areas do you serve for appliance repair?",
            answer: `We provide appliance repair throughout Palm Beach and Broward Counties, including ${listNames(SERVICE_AREA_NAMES)}.`,
        },
    ],
};

// City pages cover both services. Shared sections come from here; everything unique
// (intro, local notes, FAQs) comes from the city file.
const CITY_SERVICE_TILES = [
    { icon: "/assets/icons/flame.png", title: "Dryer vent cleaning", desc: "Full vent run cleaned, roof vents included, and airflow tested." },
    { icon: "/assets/icons/dryer.png", title: "Dryer and washer repair", desc: "No heat, won't spin or drain, leaks, noises, and error codes." },
    { icon: "/assets/icons/fridge.png", title: "Refrigerator repair", desc: "Not cooling, leaking, ice maker problems, and noisy fans." },
    { icon: "/assets/icons/support.png", title: "Ovens and dishwashers", desc: "Burners and elements that won't heat, and dishwashers that won't drain." },
];

const CITY_SHARED_FAQS = [VENT_FAQS[0], applianceRepair.faqs[3]];

const cityPages = CITIES.map((city) => ({
    path: cityPath(city.slug),
    serviceName: "Dryer Vent Cleaning and Appliance Repair",
    city: city.name,
    parent: { label: "Dryer Vent Cleaning", path: "/dryer-vent-cleaning" },
    title: city.seoTitle || `Dryer Vent Cleaning & Appliance Repair in ${city.name}, FL | Quick Vents`,
    description:
        city.seoDescription ||
        `Dryer vent cleaning and appliance repair in ${city.name}, FL. Licensed and insured, with a clear quote before any work. Book online or call (561) 929-5476.`,
    eyebrow: `${city.name}, FL`,
    h1: `Dryer Vent Cleaning & Appliance Repair in ${city.name}, FL`,
    intro: city.intro,
    bookingCard: {
        heading: `Book in ${city.name}`,
        points: ["Dryer vent cleaning and appliance repair", "Diagnosis and a clear quote first", "Tested before we leave"],
    },
    highlights: {
        heading: `Serving ${city.name} homes`,
        lead: city.local || [],
        items: CITY_SERVICE_TILES,
        cta: `Book in ${city.name}`,
    },
    sections: [],
    steps: VENT_STEPS,
    stepsLead: "From booking to the final check, here's exactly what happens.",
    // City pages skip the shared checklist to keep them short and city-specific.
    listBlocks: [],
    showCities: false,
    faqs: [...(city.faqs || []), ...CITY_SHARED_FAQS],
}));

export const SERVICE_PAGES = [dryerVentCleaning, applianceRepair, ...cityPages];
